import type { SVGProps } from "react";

// Lucide.dev Icons are preferred - go to their icon directory, set the customiser
// parameters to Stroke Width: 1.75 and Size to 24px, then make sure absolute stroke width is set to OFF, then COPY the SVG, take only the path from inside the <svg> tag, and create an IconWrapper component below. - Matt

export function IconWrapper({
  children,
  height,
  className,
  width,
  viewBox = "0 0 24 24",
  disabled = false, // Add a disabled prop
  ...props
}: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <svg
      className={`${className} w-4 h-4 transition ease-in-out duration-200 ${disabled ? "opacity-15" : ""}`} // Apply Tailwind classes when disabled
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={viewBox}
      fill="none"
      strokeWidth="1.75"
      preserveAspectRatio="xMidYMid meet"
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconTablePageStart(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M3 19V5" />
      <path d="m13 6-6 6 6 6" />
      <path d="M7 12h14" />
    </IconWrapper>
  );
}

export function IconTablePageEnd(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M17 12H3" />
      <path d="m11 18 6-6-6-6" />
      <path d="M21 5v14" />
    </IconWrapper>
  );
}

export function IconTablePageNext(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="m9 18 6-6-6-6" />
    </IconWrapper>
  );
}

export function IconTablePagePrev(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="m15 18-6-6 6-6" />
    </IconWrapper>
  );
}

export function IconInputFile(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M2 15h10" />
      <path d="m9 18 3-3-3-3" />
    </IconWrapper>
  );
}

export function IconInputSearch(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <circle cx="10" cy="10" r="6" />
      <path d="M14 14L20 20" />
    </IconWrapper>
  );
}

export function IconAccounts(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <circle cx="10" cy="8" r="5" />
      <path d="M2 21a8 8 0 0 1 10.434-7.62" />
      <circle cx="18" cy="18" r="3" />
      <path d="m22 22-1.9-1.9" />
    </IconWrapper>
  );
}

export function IconClientSetup(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </IconWrapper>
  );
}

export function IconSongManagement(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M2 10v3" />
      <path d="M6 6v11" />
      <path d="M10 3v18" />
      <path d="M14 8v7" />
      <path d="M18 5v13" />
      <path d="M22 10v3" />
    </IconWrapper>
  );
}

export function IconRegistration(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </IconWrapper>
  );
}

export function IconAcknowledgements(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <rect x="3" y="5" width="6" height="6" rx="1" />
      <path d="m3 17 2 2 4-4" />
      <path d="M13 6h8" />
      <path d="M13 12h8" />
      <path d="M13 18h8" />
    </IconWrapper>
  );
}

export function IconAccounting(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </IconWrapper>
  );
}

export function IconUserManagement(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M18 21a8 8 0 0 0-16 0" />
      <circle cx="10" cy="8" r="5" />
      <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
    </IconWrapper>
  );
}

export function IconCompanySettings(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </IconWrapper>
  );
}

export function IconSystemConfig(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M8 12h8" />
      <path d="M10 11v2" />
      <path d="M8 17h8" />
      <path d="M14 16v2" />
    </IconWrapper>
  );
}

export function IconTestFeatures(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2" />
      <path d="M8.5 2h7" />
      <path d="M7 16h10" />
    </IconWrapper>
  );
}

export function DarkModeIcon(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </IconWrapper>
  );
}

export function LightModeIcon(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </IconWrapper>
  );
}

export function IconSideBarClose(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
      <path d="m16 15-3-3 3-3" />
    </IconWrapper>
  );
}

export function IconSideBarOpen(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M15 3v18" />
      <path d="m8 9 3 3-3 3" />
    </IconWrapper>
  );
}

export function IconHamburger(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </IconWrapper>
  );
}

export function IconDownloadFile(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </IconWrapper>
  );
}

export function IconLine(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="M5 12h14" />
    </IconWrapper>
  );
}

export function IconChevronUp(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="m18 15-6-6-6 6" />
    </IconWrapper>
  );
}

export function IconChevronLeft(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="m15 18-6-6 6-6" />
    </IconWrapper>
  );
}

export function IconChevronRight(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="m9 18 6-6-6-6" />
    </IconWrapper>
  );
}

export function IconChevronDown(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="m6 9 6 6 6-6" />
    </IconWrapper>
  );
}

export function IconTick(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="M20 6 9 17l-5-5" />
    </IconWrapper>
  );
}

export function IconCross(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper strokeWidth={2} {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </IconWrapper>
  );
}

export function IconPrimaryButtonArrow(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper
      strokeWidth={0.2}
      className="transition duration-200 ease-in-out fill-sentric-dark-background dark:fill-white group-active:invert"
      width={17}
      height={14}
      viewBox="0 0 17 14"
      {...props}
    >
      <path d="M10.7679 13.5L9.63085 12.5702L14.8874 7.70746H0V6.29831H14.8812L9.63085 1.42981L10.7679 0.5L17 6.29254V7.70169L10.7679 13.4942V13.5Z" />
    </IconWrapper>
  );
}

export function IconPagePrev(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper className="w-4 h-4" {...props}>
      <path d="m15 18-6-6 6-6" />
    </IconWrapper>
  );
}

export function IconRefresh(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper className="w-4 h-4" {...props}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </IconWrapper>
  );
}

export function IconValidationErrors(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M12 8v5" />
      <path d="M12 17h.01" strokeLinecap="round" strokeWidth="2" />
    </IconWrapper>
  );
}

export function IconValidationErrorsCrossed(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M12 8v5" />
      <path d="M12 17h.01" strokeLinecap="round" strokeWidth="2" />
      <path d="M2 2l20 20" strokeWidth="2" />
    </IconWrapper>
  );
}

export function IconFilter(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper className="w-4 h-4" {...props}>
      <path d="M3 6h18" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </IconWrapper>
  );
}

export function IconClose(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </IconWrapper>
  );
}

export function IconEdit(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </IconWrapper>
  );
}

export function IconExpand(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="m15 15 6 6" />
      <path d="m15 9 6-6" />
      <path d="M21 16v5h-5" />
      <path d="M21 8V3h-5" />
      <path d="M3 16v5h5" />
      <path d="m3 21 6-6" />
      <path d="M3 8V3h5" />
      <path d="M9 9 3 3" />
    </IconWrapper>
  );
}
export function IconCreate(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
    </IconWrapper>
  );
}

export function IconUserEdit(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M11.5 15H7a4 4 0 0 0-4 4v2" />
      <path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
      <circle cx="10" cy="7" r="4" />
    </IconWrapper>
  );
}

export function IconBin(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </IconWrapper>
  );
}

export function IconUser(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </IconWrapper>
  );
}

export function IconAllUsers(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </IconWrapper>
  );
}

export function IconWandSparkles(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
      <path d="m14 7 3 3" />
      <path d="M5 6v4" />
      <path d="M19 14v4" />
      <path d="M10 2v2" />
      <path d="M7 8H3" />
      <path d="M21 16h-4" />
      <path d="M11 3H9" />
    </IconWrapper>
  );
}

export function IconEllipsis(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </IconWrapper>
  );
}

export function IconCircleDashed(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>
      <path d="M10.1 2.182a10 10 0 0 1 3.8 0" />
      <path d="M13.9 21.818a10 10 0 0 1-3.8 0" />
      <path d="M17.609 3.721a10 10 0 0 1 2.69 2.7" />
      <path d="M2.182 13.9a10 10 0 0 1 0-3.8" />
      <path d="M20.279 17.609a10 10 0 0 1-2.7 2.69" />
      <path d="M21.818 10.1a10 10 0 0 1 0 3.8" />
      <path d="M3.721 6.391a10 10 0 0 1 2.7-2.69" />
      <path d="M6.391 20.279a10 10 0 0 1-2.69-2.7" />
    </IconWrapper>
  );
}

export function IconSpotify(props: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <IconWrapper {...props}>

      <path d="M128 0C57.308 0 0 57.309 0 128c0 70.696 57.309 128 128 128 70.697 0 128-57.304 128-128C256 57.314 198.697.007 127.998.007l.001-.006Zm58.699 184.614c-2.293 3.76-7.215 4.952-10.975 2.644-30.053-18.357-67.885-22.515-112.44-12.335a7.981 7.981 0 0 1-9.552-6.007 7.968 7.968 0 0 1 6-9.553c48.76-11.14 90.583-6.344 124.323 14.276 3.76 2.308 4.952 7.215 2.644 10.975Zm15.667-34.853c-2.89 4.695-9.034 6.178-13.726 3.289-34.406-21.148-86.853-27.273-127.548-14.92-5.278 1.594-10.852-1.38-12.454-6.649-1.59-5.278 1.386-10.842 6.655-12.446 46.485-14.106 104.275-7.273 143.787 17.007 4.692 2.89 6.175 9.034 3.286 13.72v-.001Zm1.345-36.293C162.457 88.964 94.394 86.71 55.007 98.666c-6.325 1.918-13.014-1.653-14.93-7.978-1.917-6.328 1.65-13.012 7.98-14.935C93.27 62.027 168.434 64.68 215.929 92.876c5.702 3.376 7.566 10.724 4.188 16.405-3.362 5.69-10.73 7.565-16.4 4.187h-.006Z" />
    </IconWrapper>
  );
}
